'use client'

import React from 'react'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { formatAmount, sideLabels } from '../lib/amm'
import { usdCompact } from '../lib/format'
import { useAlkanes } from '../lib/store'
import type { AssetRow } from '../lib/useAssets'
import { Change } from './asset-row'
import HoverDetail from './hover-detail'

/// The stat labels in render order. Held here so the loading state reserves
/// the SAME slots: the strip used to collapse from one item to eight the
/// instant data landed and shoved the whole page down.
const SLOTS = ['1w', '24h Vol', '24h Txns', 'Liquidity', 'Mkt Cap', 'Holders'] as const

/// The deep figures behind an asset — market cap from op-101 supply, holders
/// from alkanode, buy/sell splits from espo activity — as a grid that reads two
/// wide on a phone and six wide on a desktop.
///
/// Every field is null when its source cannot answer, and every null renders
/// "—". None of these is ever a zero: a holder count of 0 for an asset with
/// $14M pooled behind it would be a statement, and a false one.
export default function StatsRail({ asset }: { asset: AssetRow }) {
	const stats = useAlkanes((s) => s.assetStats)
	const isLoading = useAlkanes((s) => s.assetStatsLoading)

	const cell = 'flex min-w-0 flex-col gap-0.5 px-3 py-2.5'
	const label = 'text-xxs text-muted-foreground'
	const value = 'text-sm font-medium tabular-nums'

	const vol24 = stats?.volume_24h_usd ?? asset.volume_24h_usd ?? null
	const liq = stats?.liquidity_usd ?? asset.liquidity_usd ?? null

	const buySell =
		stats?.volume_buy_24h_usd !== null &&
		stats?.volume_buy_24h_usd !== undefined &&
		stats?.volume_sell_24h_usd !== null &&
		stats?.volume_sell_24h_usd !== undefined
			? { buy: stats.volume_buy_24h_usd, sell: stats.volume_sell_24h_usd }
			: null
	const txnSplit =
		stats?.txns_buy_24h !== null &&
		stats?.txns_buy_24h !== undefined &&
		stats?.txns_sell_24h !== null &&
		stats?.txns_sell_24h !== undefined
			? { buy: stats.txns_buy_24h, sell: stats.txns_sell_24h }
			: null

	if (isLoading && !stats) {
		return (
			<div
				data-testid="alkanes-stats-rail"
				className="grid grid-cols-2 divide-x divide-y divide-border border-t border-border sm:grid-cols-3 lg:grid-cols-6 lg:divide-y-0"
			>
				{SLOTS.map((name) => (
					<div key={name} className={cell}>
						<span className={label}>{name}</span>
						{/* h-5 is the line box the real value occupies, so nothing
						    below this moves when the data lands. */}
						<Skeleton className="h-5 w-16" />
					</div>
				))}
			</div>
		)
	}

	return (
		<dl
			data-testid="alkanes-stats-rail"
			className="grid grid-cols-2 divide-x divide-y divide-border border-t border-border sm:grid-cols-3 lg:grid-cols-6 lg:divide-y-0"
		>
			<div className={cell}>
				<dt className={label}>1w</dt>
				<dd className={value}>
					<Change v={stats?.change_7d_pct ?? null} />
				</dd>
			</div>

			<div className={cell}>
				<dt className={label}>24h Vol</dt>
				<dd className={cn(value, vol24 === null && 'text-muted-foreground')}>
					<HoverDetail
						heading="24h volume"
						detail={
							buySell ? (
								<React.Fragment>
									<span className="tabular-nums text-success-ink">
										{usdCompact(buySell.buy)} {sideLabels(asset.id).buys}
									</span>
									<span className="tabular-nums text-destructive-ink">
										{usdCompact(buySell.sell)} {sideLabels(asset.id).sells}
									</span>
								</React.Fragment>
							) : null
						}
					>
						{usdCompact(vol24)}
					</HoverDetail>
					{buySell && (
						<span className="block text-xxs tabular-nums text-muted-foreground">
							<span className="text-success-ink">{usdCompact(buySell.buy)}</span>
							{' · '}
							<span className="text-destructive-ink">{usdCompact(buySell.sell)}</span>
						</span>
					)}
				</dd>
			</div>

			<div className={cell}>
				<dt className={label}>24h Txns</dt>
				<dd className={cn(value, (stats?.txns_24h ?? null) === null && 'text-muted-foreground')}>
					{stats?.txns_24h ?? '—'}
					{txnSplit && (
						<span className="block text-xxs tabular-nums text-muted-foreground">
							<span className="text-success-ink">{txnSplit.buy}</span>
							{' · '}
							<span className="text-destructive-ink">{txnSplit.sell}</span>
						</span>
					)}
				</dd>
			</div>

			<div className={cell}>
				<dt className={label}>Liquidity</dt>
				<dd className={value}>
					<HoverDetail
						heading="Pooled liquidity"
						align="left"
						detail={
							stats?.pools?.length ? (
								<React.Fragment>
									{/* Top 10 by TVL — DIESEL trades in 36 pools and the full
									    list overflowed the viewport; the tail is a sum. */}
									{stats.pools.slice(0, 10).map((p) => (
										<span key={p.pool_id} className="flex items-center justify-between gap-3">
											<span className="truncate text-muted-foreground">{p.name || p.pool_id}</span>
											<span className="shrink-0 tabular-nums">{usdCompact(p.tvl_usd)}</span>
										</span>
									))}
									{stats.pools.length > 10 && (
										<span className="flex items-center justify-between gap-3 border-t border-border pt-1">
											<span className="text-muted-foreground">
												+{stats.pools.length - 10} more pools
											</span>
											<span className="shrink-0 tabular-nums">
												{usdCompact(
													stats.pools.slice(10).reduce((a, p) => a + (p.tvl_usd ?? 0), 0)
												)}
											</span>
										</span>
									)}
								</React.Fragment>
							) : null
						}
					>
						{usdCompact(liq)}
					</HoverDetail>
					{asset.pools > 1 && (
						<span className="block text-xxs text-muted-foreground">{asset.pools} pools</span>
					)}
				</dd>
			</div>

			<div className={cell}>
				<dt className={label}>Mkt Cap</dt>
				<dd
					className={cn(value, (stats?.market_cap_usd ?? null) === null && 'text-muted-foreground')}
				>
					<HoverDetail
						heading="Circulating supply"
						detail={
							stats?.supply ? (
								<span className="tabular-nums">
									{formatAmount(BigInt(stats.supply), 0)} {asset.name || asset.id}
								</span>
							) : null
						}
					>
						{usdCompact(stats?.market_cap_usd ?? null)}
					</HoverDetail>
				</dd>
			</div>

			<div className={cell}>
				<dt className={label}>Holders</dt>
				<dd className={cn(value, (stats?.holders ?? null) === null && 'text-muted-foreground')}>
					{stats?.holders !== null && stats?.holders !== undefined
						? stats.holders.toLocaleString('en-US')
						: '—'}
					{stats?.holders_change_24h_pct !== null &&
						stats?.holders_change_24h_pct !== undefined && (
							<span className="block text-xxs">
								<Change v={stats.holders_change_24h_pct} />
							</span>
						)}
				</dd>
			</div>
		</dl>
	)
}
